import React, { useState, useRef, useEffect } from 'react';
import { Modal, Segment, Header, Icon, Button } from 'semantic-ui-react';
const ImageWithAnnotations = ({ imageUrl, imageName, visionData, onError, onLoad }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [selectedText, setSelectedText] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [hoveredAnnotation, setHoveredAnnotation] = useState(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    useEffect(() => {
        if (imageRef.current && imageLoaded) {
            const rect = imageRef.current.getBoundingClientRect();
            setImageDimensions({
                width: rect.width,
                height: rect.height
            });
        }
    }, [imageLoaded]);
    const handleImageLoad = () => {
        setImageLoaded(true);
        if (onLoad)
            onLoad();
    };
    const handleImageError = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.warn(`Failed to load image: ${imageName}`);
        if (onError)
            onError('Failed to load image - file may be corrupted or inaccessible');
    };
    const handleAnnotationClick = (text) => {
        setSelectedText(text);
        setModalOpen(true);
    };
    const renderAnnotations = () => {
        if (!visionData?.textAnnotations || !imageLoaded || !imageRef.current) {
            return null;
        }
        const imageElement = imageRef.current;
        const imageRect = imageElement.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect)
            return null;
        // Get the natural dimensions of the image
        const naturalWidth = imageElement.naturalWidth;
        const naturalHeight = imageElement.naturalHeight;
        // Calculate scaling factors
        const scaleX = imageRect.width / naturalWidth;
        const scaleY = imageRect.height / naturalHeight;
        // Skip the first annotation (it's usually the full text)
        const annotations = visionData.textAnnotations.slice(1);
        return annotations.map((annotation, index) => {
            if (!annotation.boundingPoly?.vertices || !annotation.description) {
                return null;
            }
            const vertices = annotation.boundingPoly.vertices;
            if (vertices.length < 4)
                return null;
            // Calculate bounding box
            const minX = Math.min(...vertices.map(v => v.x || 0));
            const minY = Math.min(...vertices.map(v => v.y || 0));
            const maxX = Math.max(...vertices.map(v => v.x || 0));
            const maxY = Math.max(...vertices.map(v => v.y || 0));
            // Apply scaling to match the displayed image size
            const left = minX * scaleX;
            const top = minY * scaleY;
            const width = (maxX - minX) * scaleX;
            const height = (maxY - minY) * scaleY;
            return (React.createElement("div", { key: index, style: {
                    position: 'absolute',
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    border: hoveredAnnotation === index ? '2px solid #2185d0' : '1px solid rgba(33, 133, 208, 0.6)',
                    backgroundColor: hoveredAnnotation === index ? 'rgba(33, 133, 208, 0.2)' : 'rgba(33, 133, 208, 0.1)',
                    cursor: 'pointer',
                    borderRadius: '2px',
                    transition: 'all 0.2s ease',
                    zIndex: hoveredAnnotation === index ? 10 : 1
                }, onClick: () => handleAnnotationClick(annotation.description), onMouseEnter: () => setHoveredAnnotation(index), onMouseLeave: () => setHoveredAnnotation(null), title: `Click to view: "${annotation.description}"` }));
        });
    };
    return (React.createElement("div", { style: { position: 'relative', display: 'inline-block' } },
        React.createElement("div", { ref: containerRef, style: { position: 'relative', display: 'inline-block' } },
            React.createElement("img", { ref: imageRef, src: imageUrl, alt: imageName, style: {
                    maxWidth: '100%',
                    maxHeight: '600px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'block'
                }, onLoad: handleImageLoad, onError: handleImageError }),
            renderAnnotations()),
        React.createElement(Modal, { open: modalOpen, onClose: () => setModalOpen(false), size: "small", closeIcon: true },
            React.createElement(Header, null,
                React.createElement(Icon, { name: "text cursor" }),
                "Extracted Text"),
            React.createElement(Modal.Content, null,
                React.createElement(Segment, null,
                    React.createElement("pre", { style: {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '1.1rem',
                            lineHeight: '1.4'
                        } }, selectedText))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: () => setModalOpen(false), primary: true },
                    React.createElement(Icon, { name: "checkmark" }),
                    "Close"))),
        visionData?.textAnnotations && imageLoaded && (React.createElement("div", { style: {
                marginTop: '10px',
                fontSize: '0.9rem',
                color: '#666',
                fontStyle: 'italic'
            } },
            React.createElement(Icon, { name: "info circle", size: "small" }),
            "Hover over highlighted areas to see text detection. Click to view extracted text.")),
        !visionData?.textAnnotations && imageLoaded && (React.createElement("div", { style: {
                marginTop: '10px',
                fontSize: '0.9rem',
                color: '#999',
                fontStyle: 'italic'
            } },
            React.createElement(Icon, { name: "warning circle", size: "small" }),
            "Text annotations not available for this image."))));
};
export default ImageWithAnnotations;
